import {createObjectPropertyUpdater, createUpdater} from '../../../../srcCommon/helpers/updateHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {ApiWorkspace, WorkspaceModel} from '../../../models/api/workspaceModel';
import {putApiAsync} from '../../../services/http/http';
import {HttpError} from '../../../services/http/httpError';
import {workspaceLock} from '../../../services/locks/resourceLocks';
import {ThunkAsyncAction} from '../../appThunk';
import {optimisticUpdateAsyncAction} from '../../optimisticUpdates/optimisticUpdatesActions';
import {DataState} from '../dataState';
import {getUser} from '../teammates/teammatesSelectors';
import {getWorkspace} from './workspaceSelectors';

/*
 * Action types.
 */

export enum WorkspaceActionsEnum {
  WORKSPACE_UPDATED = '@@data/workspace/WORKSPACE_UPDATED',
}

/*
 * Types.
 */

interface WorkspaceUpdatedAction {
  type: WorkspaceActionsEnum.WORKSPACE_UPDATED;
  payload: Pick<WorkspaceModel, 'updatedAt' | 'value'>;
}

export type WorkspaceUpdater = (existingWorkspace: WorkspaceModel) => WorkspaceModel;

/*
 * Action creators.
 */

export function workspaceUpdatedAsyncAction(updateWorkspace: WorkspaceUpdater): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const optimisticUpdater = createObjectPropertyUpdater<DataState, 'workspace'>(
      'workspace',
      createUpdater<WorkspaceModel>((workspace) => updateWorkspace(workspace)),
    );

    await dispatch(
      optimisticUpdateAsyncAction(optimisticUpdater, async () =>
        workspaceLock.lockAsync(async () => {
          const user = getUser(state);
          const workspaceUrl = `${user.url}/workspace`;
          const newWorkspace = updateWorkspace(getWorkspace(state));
          const body = ApiWorkspace.serialize(newWorkspace);

          try {
            const serverWorkspace = await putApiAsync(ApiWorkspace, workspaceUrl, body);
            const updatedWorkspaceAction: WorkspaceUpdatedAction = {
              type: WorkspaceActionsEnum.WORKSPACE_UPDATED,
              payload: {
                updatedAt: serverWorkspace.updatedAt,
                value: serverWorkspace.value,
              },
            };

            return updatedWorkspaceAction;
          } catch (error) {
            if (!(error instanceof HttpError)) {
              reportToBugsnag(error);
            }

            return undefined;
          }
        }),
      ),
    );
  };
}

/*
 * Union type.
 */

export type WorkspaceAction = WorkspaceUpdatedAction;
